import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { Box } from "@mui/material";
import Header from "./components/Header/Header";
import socket from "./socket";
import { initializePushNotifications, showBrowserNotification } from "./utils/pushNotification";
import NotificationManager from "./utils/NotificationManager";

function getUserIdentity() {
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    return {
      userId: storedUser?._id,
      role: storedUser?.role,
    };
  } catch {
    return { userId: null, role: null };
  }
}

const PushSetup = () => {
  // ── Track current user identity to detect login/logout/switch ──
  const [userIdentity, setUserIdentity] = useState(() => getUserIdentity());

  // ── Re-check user on storage changes (login/logout in another tab) ──
  useEffect(() => {
    const checkUser = () => setUserIdentity(getUserIdentity());
    window.addEventListener('storage', checkUser);
    // Also poll on focus/visibility change since localStorage changes
    // from the same tab don't fire 'storage' event
    const onFocus = () => {
      const current = getUserIdentity();
      if (current.userId !== userIdentity.userId || current.role !== userIdentity.role) {
        setUserIdentity(current);
      }
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('storage', checkUser);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [userIdentity]);

  // ── Deduplication refs ──
  const seenKeysRef = useRef(new Set());
  const prevIdentityRef = useRef({ userId: null, role: null });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const { userId, role } = userIdentity;
    const prev = prevIdentityRef.current;

    // ── If user changed, clean up old socket state ──
    if (prev.userId && prev.userId !== userId) {
      // Leave old rooms
      if (prev.role === 'vendor') {
        const oldVendorProfile = JSON.parse(localStorage.getItem('vendorProfile') || '{}');
        const oldVendorRoomId = oldVendorProfile?._id || prev.userId;
        socket.emit('vendor_leave', oldVendorRoomId);
        socket.emit('vendor_leave', prev.userId);
      }
      if (prev.role === 'agent') {
        socket.emit('agent_leave', prev.userId);
      }
      // Clear deduplication for old user
      const oldDedupeKey = `notification-dedupe-${prev.role}`;
      try { localStorage.removeItem(oldDedupeKey); } catch {}
      seenKeysRef.current.clear();
    }

    prevIdentityRef.current = userIdentity;

    if (!userId || !role || !['vendor', 'agent'].includes(role)) return;

    void initializePushNotifications();

    const vendorProfile = JSON.parse(localStorage.getItem('vendorProfile') || '{}');
    const vendorRoomId = vendorProfile?._id || userId;
    const userRoomId = userId;
    const agentRoomId = userId;

    const emitRooms = () => {
      if (role === 'vendor' && vendorRoomId) {
        socket.emit('vendor_join', vendorRoomId);
        if (userRoomId && userRoomId !== vendorRoomId) {
          socket.emit('vendor_join', userRoomId);
        }
      }
      if (role === 'agent' && agentRoomId) {
        socket.emit('agent_join', agentRoomId);
      }
    };

    emitRooms();
    socket.on('connect', emitRooms);

    // ── Global socket notification handler ──
    const dedupeStorageKey = `notification-dedupe-${role}`;

    try {
      const stored = JSON.parse(localStorage.getItem(dedupeStorageKey) || '[]');
      stored.forEach((key) => seenKeysRef.current.add(key));
    } catch (err) {
      console.warn('Failed to load notification dedupe keys', err);
    }

    const saveDedupeKey = (key) => {
      if (!key) return false;
      if (seenKeysRef.current.has(key)) return true;
      seenKeysRef.current.add(key);
      setTimeout(() => {
        try {
          const stored = JSON.parse(localStorage.getItem(dedupeStorageKey) || '[]');
          if (!stored.includes(key)) {
            stored.push(key);
            localStorage.setItem(dedupeStorageKey, JSON.stringify(stored.slice(-200)));
          }
        } catch (err) {
          console.warn('Failed to persist notification dedupe key', err);
        }
      }, 0);
      return false;
    };

    const handleNewOrderNotification = async (notification) => {
      if (!notification) return;
      const orderId = notification.orderId || notification._id;
      const dedupeKey = notification.tag ? `${role}-tag-${notification.tag}` : `${role}-order-${orderId}`;

      // Always play bell sound — deduplication only prevents duplicate browser notifications
      window.dispatchEvent(new CustomEvent('app:play-bell'));

      if (saveDedupeKey(dedupeKey)) return;

      const notifUrl = role === 'vendor' ? '/vendor/orders' : '/agent/orders?tab=notifications';
      const payload = notification.browserNotification || {
        title: notification.title || 'New Order Available',
        body: `${notification.vendorName || 'A vendor'} has a new order for you.`,
        orderId,
        data: { url: notifUrl },
        tag: `order-${orderId}`,
      };

      await showBrowserNotification(payload.title, payload.body, {
        orderId: payload.orderId,
        data: payload.data,
        tag: payload.tag,
        requireInteraction: payload.requireInteraction ?? true,
      });
    };

    const handleOrderAssigned = async (data) => {
      if (!data) return;
      const dedupeTag = `assigned-${data?.orderId ?? Date.now()}`;

      // Always play bell sound
      window.dispatchEvent(new CustomEvent('app:play-bell'));

      if (saveDedupeKey(dedupeTag)) return;

      const assignedUrl = role === 'vendor' ? '/vendor/orders' : '/agent/orders?tab=notifications';
      await showBrowserNotification(
        data?.title || 'New Order Received',
        data?.body || (role === 'vendor' ? 'A new order has been assigned to you.' : 'An order has been assigned to you.'),
        {
          orderId: data?.orderId,
          data: { url: assignedUrl },
          tag: dedupeTag,
          requireInteraction: true,
          vibrate: [],
        }
      );
    };

    socket.on('new_order_notification', handleNewOrderNotification);
    socket.on('order_assigned', handleOrderAssigned);

    return () => {
      socket.off('connect', emitRooms);
      socket.off('new_order_notification', handleNewOrderNotification);
      socket.off('order_assigned', handleOrderAssigned);
    };
  }, [userIdentity]);

  return null;
};

const Layout = () => {
  return (
    <>
      <PushSetup />
      <NotificationManager />
      <Header />
      <Box
        sx={{
          pt: { xs: '112px', md: '64px' },
          minHeight: '100dvh',
        }}
      >
        <Outlet />
      </Box>
    </>
  );
};

export default Layout;